import React from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useLocation } from 'react-router-dom';

import LoadingScreen from './LoadingScreen';
import { useAuth } from '../contexts/AuthContext';

const RoleGuard = ({ 
  children, 
  allowedRoles = [], 
  redirectTo = null,
  fallbackPath = null 
}) => {
  const { user, userProfile, loading } = useAuth();
  const location = useLocation();
  const { t } = useTranslation('common');

  // Show loading while authentication is being resolved
  if (loading) {
    return <LoadingScreen />;
  }

  // If user is not authenticated, redirect to appropriate login
  if (!user) {
    const isAdminRoute = location.pathname.startsWith('/admin');
    const loginPath = isAdminRoute ? '/admin-login' : '/tenant-login';
    return <Navigate to={loginPath} replace />;
  }

  // If user profile is not loaded yet, show loading
  if (!userProfile) {
    return <LoadingScreen />;
  }

  // Get user role from profile
  const userRole = userProfile?.role || user?.user_metadata?.role;

  // Check if user has required role
  const hasRequiredRole = allowedRoles.includes(userRole);

  if (!hasRequiredRole) {
    // If redirectTo is specified, use it
    if (redirectTo) {
      return <Navigate to={redirectTo} replace />;
    }

    // If fallbackPath is specified, use it
    if (fallbackPath) {
      return <Navigate to={fallbackPath} replace />;
    }

    // Default redirection based on user role
    if (userRole === 'tenant') {
      return <Navigate to="/tenant-dashboard" replace />;
    } else if (userRole === 'admin') {
      return <Navigate to="/admin-dashboard" replace />;
    }

    // If no role or unknown role, redirect to tenant login
    return <Navigate to="/tenant-login" replace />;
  }

  // User has required role, render the protected component
  return children;
};

export default RoleGuard;
